import { FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import { useAppDispatch } from "@redux/hooks";
import { setAccessToken, setCorporateSelection } from "@redux/slices/AuthSlice";
import { PAGE_URLS } from "@constants/common";
import { EmailRegex } from "@utils/regex";
import { useLoginMutation } from "../authApi";
import {
  LoginInfoIcon,
  LoginMailIcon,
  PasswordHide,
  PasswordView,
  YggLogo,
} from "@features/common/SvgIcons";
import Button from "@features/common/button/Button";
import WarningBlock from "@features/common/warningBlock/WarningBlock";
import HelpText from "../helpText/HelpText";
import FbWarning from "../fbWarning/FbWarning";
import styles from "../loginForm/LoginForm.module.scss";
import { handleRedirectByTab } from "@utils/redirectUtils";

// ============================================================================
// Types & Interfaces
// ============================================================================

interface PasswordLoginProps {
  onToggleToLoginForm: () => void;
  onPasswordLoginSuccess: () => void;
  verifiedEmail: string;
  setOtpUrls: (actions: any) => void;
}

const PasswordLogin: FC<PasswordLoginProps> = ({
  onToggleToLoginForm,
  onPasswordLoginSuccess,
  verifiedEmail,
  setOtpUrls,
}) => {
  // ============================================================================
  // Hooks
  // ============================================================================

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");

  // ============================================================================
  // State Management
  // ============================================================================

  const [email, setEmail] = useState(verifiedEmail);
  const [password, setPassword] = useState("");
  const [emailHelperText, setEmailHelperText] = useState("");
  const [passwordHelperText, setPasswordHelperText] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [showFbWarning, setShowFbWarning] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState("");

  // ============================================================================
  // API Hooks
  // ============================================================================

  const [
    login,
    {
      data: loginData,
      error: loginError,
      isSuccess: loginIsSuccess,
      isLoading: loginLoading,
    },
  ] = useLoginMutation();

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const clearFormErrors = () => {
    setEmailHelperText("");
    setPasswordHelperText("");
  };

  const validateForm = () => {
    if (!EmailRegex.test(email)) {
      setEmailHelperText("Invalid Email");
      return false;
    }
    return true;
  };

  const handleFBLogin = () => {
    setShowFbWarning(true);
  };

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleRedirect = () => {
    navigate(
      `${PAGE_URLS.FORGOT_PASSWORD}/?email=${encodeURIComponent(email)}`,
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFormErrors();

    if (validateForm()) {
      try {
        login({ email, password });
      } catch (err) {
        console.error("Login failed:", err);
      }
    }
  };

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Sync email state with verifiedEmail prop
   * Important for business login flow where email might come from URL params
   */
  useEffect(() => {
    if (verifiedEmail) {
      setEmail(verifiedEmail);
    }
  }, [verifiedEmail]);

  /**
   * Controls submit button disabled state
   * Button is enabled only when both email and password are provided
   */
  useEffect(() => {
    setIsSubmitDisabled(!(email && password));
  }, [email, password]);

  /**
   * Handles login API response and post-login routing
   *
   * Success flow:
   * 1. Store access token in Redux
   * 2. Store OTP URLs for potential 2FA verification
   * 3. Route based on configuration:
   *    - If 2FA enabled: transition to OTP screen
   *    - If corporate selection required: navigate to corporate selection page
   *    - Otherwise: navigate to main app (issue credits page)
   *
   * Error flow:
   * - Display error message in password field
   */
  useEffect(() => {
    if (loginIsSuccess && loginData?.data) {
      setOtpUrls(loginData?.data?.actions);
      dispatch(
        setCorporateSelection(!!loginData?.data?.show_corporate_selection),
      );
      // Store access token in Redux store if present
      if (loginData?.data?.access_token) {
        dispatch(setAccessToken(loginData?.data?.access_token));
      }

      // Check if 2FA (Two-Factor Authentication) is enabled
      if (loginData?.data?.is_2fa_enabled) {
        // Transition to OTP verification screen
        onPasswordLoginSuccess();
        return;
      }

      const decodedRedirectUrl = redirectUrl
        ? decodeURIComponent(redirectUrl)
        : null;

      // Check if user needs to select a corporate entity
      if (loginData?.data?.show_corporate_selection) {
        const corporateUrl = decodedRedirectUrl
          ? `${PAGE_URLS.SELECT_CORPORATE}?redirect=${encodeURIComponent(
              decodedRedirectUrl,
            )}`
          : PAGE_URLS.SELECT_CORPORATE;

        navigate(corporateUrl);
        return;
      } else {
        localStorage.setItem("show_corporate_selection", "true"); // Disable corporate selection for users with only one corporate
      }

      // Check if user has direct access via redirect URL
      if (decodedRedirectUrl) {
        window.location.href = decodedRedirectUrl;
        return;
      }
      // User has direct access, navigate to main app
      //
      handleRedirectByTab(
        loginData?.data?.redirect_tab || PAGE_URLS.ISSUE_CREDITS,
        navigate,
      );
    } else if (loginError) {
      // Extract error message from API response
      const errorMessage =
        (loginError as any)?.data?.message ||
        (loginError as any)?.data?.errors?.message ||
        "Login failed. Please try again.";
      setPasswordHelperText(errorMessage);
    }
  }, [
    loginIsSuccess,
    loginData,
    dispatch,
    navigate,
    onPasswordLoginSuccess,
    loginError,
  ]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className={styles["signin-email"]}>
      {/* Header Section */}
      <div className={styles["header"]}>
        <YggLogo />
        <h5>Enter Password</h5>
      </div>

      {/* Password Login Form */}
      <form className="email-login" onSubmit={handleSubmit}>
        {/* Email Field (Read-only with Edit button) */}
        <div
          className={`${styles["email-input-button"]} ${
            emailHelperText ? styles["error-outline"] : ""
          }`}
        >
          <div className={styles["email-input-button__input"]}>
            <LoginMailIcon />
            <FormControl variant="standard" fullWidth>
              <TextField
                autoFocus={false}
                value={verifiedEmail}
                name="email"
                id="email"
                type="text"
                disabled
                onChange={(e) => setEmail(e.target.value)}
                onFocus={clearFormErrors}
                variant="outlined"
                placeholder={verifiedEmail}
                inputProps={{ maxLength: 150 }}
              />
            </FormControl>
          </div>

          {/* Edit Button - Returns to email entry screen */}
          <div
            className={`button-container-email ${styles["button-container-email"]}`}
          >
            <Button
              theme="purple"
              className={`submit-button ${styles["email-input-button__button"]}`}
              action={onToggleToLoginForm}
              attribues={{ type: "button" }}
            >
              Edit
            </Button>
          </div>
        </div>

        {/* Email Error Message */}
        {emailHelperText && (
          <FormHelperText error id="email-helper-text">
            {emailHelperText}
          </FormHelperText>
        )}

        {/* Password Field */}
        <div className={styles["password-field"]}>
          <div
            className={`${styles["password-input-button"]} ${
              passwordHelperText ? styles["error-outline"] : ""
            }`}
          >
            <div className={styles["password-input-button__input"]}>
              <FormControl variant="standard" fullWidth>
                <TextField
                  autoFocus
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowWarning("");
                  }}
                  onFocus={() => {
                    setPasswordHelperText("");
                    setShowWarning("");
                  }}
                  id="password"
                  type={showPassword ? "password" : "text"}
                  variant="outlined"
                  placeholder="Enter your password"
                />
              </FormControl>

              {/* Password Visibility Toggle */}
              {password?.length > 0 && (
                <button
                  type="button"
                  onClick={handleTogglePasswordVisibility}
                  className={styles["password-visibility-toggle"]}
                >
                  {showPassword ? <PasswordHide /> : <PasswordView />}
                </button>
              )}
            </div>
          </div>

          {/* Password Error Message & Forgot Password Link */}
          <div
            className={`${styles["password-field-remember"]} ${
              passwordHelperText ? styles["double"] : styles["single"]
            } password-helper-text`}
            style={{ cursor: "pointer" }}
          >
            {passwordHelperText && (
              <FormHelperText error id="password-helper-text">
                {passwordHelperText}
              </FormHelperText>
            )}

            {!showWarning?.length && (
              <span onClick={handleRedirect}>Forgot Password?</span>
            )}
          </div>
        </div>

        {/* Warning Block (for additional warnings if needed) */}
        {showWarning && <WarningBlock message={showWarning} />}

        {/* Submit Button */}
        {!showWarning?.length && (
          <Button
            id="btnSubmit"
            theme="purple"
            className={`width-100 submit-button ${styles["email-input-button__button"]}`}
            attribues={{
              disabled: isSubmitDisabled || loginLoading,
              type: "submit",
            }}
          >
            Continue
          </Button>
        )}
      </form>

      {/* Help Section */}
      <div className={styles["trouble-signin-container"]}>
        <div className={styles["trouble-signin"]}>
          <LoginInfoIcon />
          <span className={styles["content"]} onClick={handleFBLogin}>
            Trouble signing in?
          </span>
        </div>
        <HelpText />
      </div>

      {/* Facebook Warning Dialog */}
      <FbWarning open={showFbWarning} onClose={setShowFbWarning} />
    </div>
  );
};

export default PasswordLogin;
